import {
  applyRangeGroupingsToSpecParts,
  getActiveRangeGroupings,
  isGroupingColumn,
} from "./rangeGroupingUtils";
import { SpecBuilder } from "./specBuilderSupport";

/**
 * Builds a report specification from a base spec and a tab's current state.
 * Single source of truth for how user state is applied to the saved base spec
 * — used for both single-table reports and every tab of a MULTI_TABLE_REPORT.
 *
 * @param {object} baseSpec  The saved base specification for this tab.
 * @param {object} tabState  Working state from useReportState (pinnedColumns,
 *   columnOrder, columnVisibilityModel, columnNameOverrides, columnDecimals,
 *   sortModel, filters, preAggregationFilters, searchQuery, groupByColumns, columnAggregations,
 *   extraAggregateColumns, removedExtraAggregateColumns, includeDetails,
 *   includeTotal, rangeGroupings — source column → grouping column aggregate
 *   entry).  removedExtraAggregateColumns names deleted extra aggregate columns,
 *   which are stripped from every part of the base spec.
 * @param {object} [opts]
 * @param {Set<string>}  [opts.notForExportColumns]  Columns that must pass through
 *   even when hidden.  Derived from baseSpec when absent.
 * @param {object}  [opts.metadataDisplayNames]  internalName→displayName from server
 *   metadata.  Defaults to {}.
 * @param {Array}   [opts.columnsForGrouping]  columnsData-style entries (with _type,
 *   _subType) needed to infer default aggregation functions.  Required for a full
 *   grouping rebuild (active tab).  When absent, base-spec aggregates are preserved.
 * @param {string}  [opts.searchQuery]  Overrides tabState.searchQuery.  Active tab
 *   passes the debounced value here.
 */
export const buildSpecFromTabState = (baseSpec, tabState, opts = {}) => {
  const {
    notForExportColumns: notForExportProp,
    metadataDisplayNames = {},
    columnsForGrouping = [],
    searchQuery: searchQueryOpt,
  } = opts;

  const {
    pinnedColumns = [],
    columnOrder = [],
    columnVisibilityModel = {},
    columnNameOverrides = {},
    columnDecimals = {},
    sortModel = [],
    filters = [],
    preAggregationFilters,
    searchQuery: tabSearchQuery = "",
    groupByColumns = new Set(),
    columnAggregations = {},
    extraAggregateColumns = [],
    removedExtraAggregateColumns = new Set(),
    includeDetails = false,
    includeTotal = false,
    includeSubTotal = false,
    allValuesEnumerations = new Set(),
    summarySelections = {},
    summaryType = "BOTTOM",
    rangeGroupings = {},
  } = tabState;

  const searchQuery =
    searchQueryOpt !== undefined ? searchQueryOpt : tabSearchQuery;

  const notForExportSet =
    notForExportProp ??
    new Set(
      (baseSpec?.outputColumns ?? [])
        .filter((oc) => oc.notForExport)
        .map((oc) => oc.column)
    );
  const pinnedSet = new Set(pinnedColumns);
  const hiddenSet = new Set(
    Object.entries(columnVisibilityModel)
      .filter(([, v]) => v === false)
      .map(([col]) => col)
  );
  const visibleSet = new Set(
    Object.entries(columnVisibilityModel)
      .filter(([, v]) => v === true)
      .map(([col]) => col)
  );
  const isRemoved = (column) => removedExtraAggregateColumns.has(column);
  const savedOutputCols = (baseSpec?.outputColumns ?? []).filter(
    (oc) => !isRemoved(oc.column)
  );
  const savedOutputColMap = new Map(
    savedOutputCols.map((oc) => [oc.column, oc])
  );
  const baseHasGrouping = (baseSpec?.group ?? []).length > 0;

  let allOutputColumns;

  if (columnsForGrouping.length > 0 || !baseHasGrouping) {
    // Safe to reorder — either we have type info for a full grouping rebuild
    // or the base spec has no grouping.
    const columnOrderSet = new Set(columnOrder);
    const orderedCols = [
      ...columnOrder.filter((col) => savedOutputColMap.has(col)),
      ...savedOutputCols
        .filter((oc) => !columnOrderSet.has(oc.column))
        .map((oc) => oc.column),
    ];
    allOutputColumns = orderedCols.map((col) => {
      const savedCol = savedOutputColMap.get(col);
      const override = columnNameOverrides[col];
      const displayName =
        override === null
          ? null
          : override || metadataDisplayNames[col] || savedCol?.displayName;
      return {
        column: col,
        ...(displayName !== undefined && { displayName }),
        ...((hiddenSet.has(col) ||
          (notForExportSet.has(col) && !visibleSet.has(col))) && {
          notForExport: true,
        }),
        ...(pinnedSet.has(col) && { pinned: true }),
      };
    });
  } else {
    // Base spec has grouping and no columnsForGrouping — preserve order, just
    // apply visibility + pinning.
    allOutputColumns = savedOutputCols.map((oc) => {
      const override = columnNameOverrides[oc.column];
      const displayName = override === null ? null : override || oc.displayName;
      return {
        column: oc.column,
        ...(displayName !== undefined && { displayName }),
        ...((hiddenSet.has(oc.column) ||
          (oc.notForExport && !visibleSet.has(oc.column))) && {
          notForExport: true,
        }),
        ...(pinnedSet.has(oc.column) && { pinned: true }),
      };
    });
  }

  // Add missing source columns.
  const savedSourceCols = baseSpec?.sourceColumns ?? [];
  const existingColumnSet = new Set(allOutputColumns.map((c) => c.column));
  for (const sc of savedSourceCols) {
    if (!existingColumnSet.has(sc.column)) {
      allOutputColumns.push({
        column: sc.column,
        ...(hiddenSet.has(sc.column) && { notForExport: true }),
      });
      existingColumnSet.add(sc.column);
    }
  }

  const activeRangeGroupings = getActiveRangeGroupings(
    rangeGroupings,
    groupByColumns,
    allOutputColumns.map((c) => c.column)
  );

  // Extra aggregate columns.
  if (groupByColumns.size > 0) {
    for (const extra of extraAggregateColumns) {
      if (!existingColumnSet.has(extra.column)) {
        const sourceIdx = allOutputColumns.findIndex(
          (c) => c.column === extra.sourceColumn
        );
        const insertAt =
          sourceIdx >= 0 ? sourceIdx + 1 : allOutputColumns.length;
        allOutputColumns.splice(insertAt, 0, {
          column: extra.column,
          displayName: extra.column,
        });
        existingColumnSet.add(extra.column);
      }
    }
  }

  // Sort order.
  const order = sortModel.map(({ field, sort }) => ({
    column: field,
    direction: (sort ?? "asc").toUpperCase(),
  }));

  // Filters — exclude reverted, then map to spec form.
  const activeFilters = (filters ?? []).filter(
    (f) => f._status === undefined || f._status !== "reverted"
  );
  const specFilters = activeFilters.map((f) => f._spec ?? f);
  if (searchQuery) {
    specFilters.push({
      column: allOutputColumns
        .filter((c) => !c.notForExport)
        .map((c) => c.column),
      function: "SEARCH",
      parameters: searchQuery,
    });
  }

  // Decimal places — set only on columns with an entry in columnDecimals.
  allOutputColumns = allOutputColumns.map((col) =>
    columnDecimals[col.column] !== undefined
      ? { ...col, decimals: columnDecimals[col.column] }
      : col
  );

  // Range groupings — add active grouping columns, drop inactive ones.
  const rangeGrouped = applyRangeGroupingsToSpecParts({
    aggregate: baseSpec?.aggregate,
    outputColumns: allOutputColumns,
    order,
    filters: specFilters,
    groupColumns: groupByColumns,
    allValuesEnumerations,
    activeRangeGroupings,
  });
  const specGroupColumns = rangeGrouped.groupColumns;
  const specAllValuesEnumerations = rangeGrouped.allValuesEnumerations;

  // Build the spec.
  const builder = new SpecBuilder(baseSpec);
  builder.setAggregate(rangeGrouped.aggregate);
  builder.setOutputColumns(rangeGrouped.outputColumns);
  builder.setOrder(rangeGrouped.order);
  builder.setFilters(rangeGrouped.filters);
  if (Array.isArray(preAggregationFilters)) {
    builder.setPreAggregationFilters(
      preAggregationFilters
        .filter((f) => f._status !== "reverted")
        .map((f) => f._spec ?? f)
    );
  }

  if (specGroupColumns.size > 0) {
    if (columnsForGrouping.length > 0) {
      // Full rebuild with type info (active tab).
      builder.setGroupingFromParts(
        specGroupColumns,
        columnAggregations,
        columnsForGrouping.filter((col) => !isRemoved(col.column)),
        extraAggregateColumns,
        includeDetails,
        includeTotal,
        specAllValuesEnumerations,
        includeSubTotal
      );
    } else {
      // No type info — preserve base aggregates, apply explicit overrides + includes.
      const baseGroup = (baseSpec?.group ?? [])[0];
      const mergedAggs = (baseGroup?.aggregate ?? [])
        .filter(
          (entry) => !isGroupingColumn(entry.column) && !isRemoved(entry.column)
        )
        .map((entry) => {
          const func = columnAggregations[entry.column];
          return func
            ? { ...entry, expression: { ...entry.expression, func } }
            : entry;
        });
      const aggMap = new Map(mergedAggs.map((a) => [a.column, a]));
      for (const extra of extraAggregateColumns) {
        aggMap.set(extra.column, {
          column: extra.column,
          expression: { func: extra.func, sourceColumn: extra.sourceColumn },
        });
      }
      builder.setGrouping([
        {
          groupColumns: [...specGroupColumns],
          aggregate: [...aggMap.values()],
          includeDetails: includeDetails ?? baseGroup?.includeDetails ?? false,
          includeTotal: includeTotal ?? baseGroup?.includeTotal ?? false,
          includeSubTotal:
            includeSubTotal ?? baseGroup?.includeSubTotal ?? false,
          allValuesEnumerations: [...specAllValuesEnumerations],
        },
      ]);
    }
  } else {
    builder.setGrouping(null);
  }

  // Summary row — only included when a placement (TOP/BOTTOM) is selected and
  // at least one column has a summary aggregator or override.
  const summaryColumns = Object.keys(summarySelections).filter(
    (column) => !isRemoved(column)
  );
  if (summaryType !== "OFF" && summaryColumns.length > 0) {
    const aggregations = summaryColumns.map((column) => {
      const selection = summarySelections[column];
      if (selection.override !== undefined) {
        return { column, override: selection.override };
      }
      return {
        column,
        expression: { func: selection.func, sourceColumn: column },
      };
    });
    builder.setSummaryRow({ position: summaryType, aggregations });
  } else {
    builder.setSummaryRow(null);
  }

  return builder.build();
};
